import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import FileSaver from "file-saver";
import { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { customTheme } from "../../../../config/theme/MuiTheme";
import SectionHeader from "../../../../util/component/SectionHeader";
import { ContextOperation } from "../../context/OperationContext";
import { useAgGridCustomDatasource } from "../../hooks/useAgGridCustomDatasource";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import useSnackbar from "../../hooks/useSnackbar";
import OperationTypes from "../../types";
import { AutoCompleteOption } from "../common/AutoComplete";
import EnumChangeName from "../common/EnumChangeName";
import FormInput from "../common/FormInput";
import GenericFilterBox from "../common/GenericFilterBox";
import { nameof } from "../common/ObjectUtils";
import DeliveryNoteService from "./service/DeliveryNoteServices";
import AgGridOperationTable from "../common/AgGridOperationTable";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import useToggle from "../../hooks/useToggle";
import ExportDeliveryNotesDialog from "./dialogs/ExportDeliveryNotesDialog";
import { formatDateTable } from "../../utils";

interface Props {
  form: any;
}

export const DeliveryNoteTable = (props: Props) => {
  const { tenant, userLocations, allLocations } = useContext(ContextOperation);
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { setLoading } = useContext(ContextOperation);
  const [openDownloadModal, setOpenDownloadModal] = useToggle();

  const { locationType } = EnumChangeName();

  const t = useOperationTranslation();
  const formatedDate = "DD/MM/YYYY-HH:mm";

  //console.log('props', props );

  const [
    deliveryNoteDataSource,
    totalDeliveryNote,
    updateDeliveryNoteDataSource,
  ] = useAgGridCustomDatasource(
    DeliveryNoteService.get.filterDeliveryNote,
    [{
      tenant,
      ...props.form.getValues(),
      plate: props.form.getValues("plate")?.trim(),
      vin: props.form.getValues("vin")?.trim(),
      number: props.form.getValues("number")?.trim(),
      startStationId: allLocations.find(el => el.key === props.form.getValues("startStationId"))?.bpCode,
      startStationType: allLocations.find(el => el.key === props.form.getValues("startStationId"))?.type,
      endStationId: allLocations.find(el => el.key === props.form.getValues("endStationId"))?.bpCode,
      endStationType: allLocations.find(el => el.key === props.form.getValues("endStationId"))?.type,
    }],
    [tenant, props.form.formState.submitCount]
  );

  const startStatationDescr = (params: ICellRendererParams<OperationTypes.DeliveryNote>) => {
    const label = `${params.data?.transferRequest.startStationDescription} (${locationType(params.data?.transferRequest.startStationType || "")})`;
    return (
      <>
        <Mui.Tooltip title={label}>
          <span>{!!label ? label : ""}</span>
        </Mui.Tooltip>
      </>
    );
  };

  const expectedStatationDescr = (params: ICellRendererParams<OperationTypes.DeliveryNote>) => {
    const label = `${params.data?.transferRequest.expectedEndStationDescription} (${locationType(params.data?.transferRequest.expectedEndStationType || "")})`;
    return (
      <>
        <Mui.Tooltip title={label}>
          <span>{!!label ? label : ""}</span>
        </Mui.Tooltip>
      </>
    );
  };

  const endStatationDescr = (params: ICellRendererParams<OperationTypes.DeliveryNote>) => {
    const label = `${params.data?.transferRequest.endStationDescription} (${locationType(params.data?.transferRequest.endStationType || "")})`;
    if (!params.data?.transferRequest.endStationDescription) {
      return null;
    }
    return (
      <>
        <Mui.Tooltip title={label}>
          <span>{!!label ? label : ""}</span>
        </Mui.Tooltip>
      </>
    );
  };

  const showDetails = (id: number | undefined) => {
    if (!id) {
      snackbar.show("warning", "No id found for this row!");
      return;
    }
    const baseUrl = window.location.href;
    const safeId = encodeURIComponent(id ?? '');
    window.open(`${baseUrl}/details/${safeId}`);
  };

  const downloadPdf = (id: number | undefined) => {
    setLoading(true);
    DeliveryNoteService.get
      .downloadPdf(tenant, id)
      .then((response: any) => {
        setLoading(false);
        const filename =
          response.headers["content-disposition"].split("filename=")[1];
        const blob = new Blob([response.data], { type: "application/pdf" });
        FileSaver.saveAs(blob, filename);
      })
      .catch(() => {
        snackbar.show("error", t("vehicleExit.confirm.genericError"));
      });
  };

  const handleDownloadExcel = () => {
    setLoading(true);

    const values = props.form.getValues();    
    const startStationId = allLocations.find(el => el.key === props.form.getValues("startStationId"))?.bpCode;
    const startStationType = allLocations.find(el => el.key === props.form.getValues("startStationId"))?.type;
    const endStationId = allLocations.find(el => el.key === props.form.getValues("endStationId"))?.bpCode;
    const endStationType = allLocations.find(el => el.key === props.form.getValues("endStationId"))?.type;

    DeliveryNoteService.get
      .exportDeliveryNote(
        {
          tenant, 
          ...values,
          startStationId,
          startStationType,
          endStationId,
          endStationType
      })
      .then((response) => {
        setLoading(false);
        if (response.status === 200) {
          const filename =
          response.headers["content-disposition"].split("filename=")[1];
        var blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        FileSaver.saveAs(blob, filename);
        } else setOpenDownloadModal();
        
      })
      .catch((error: any) => {
        setLoading(false);

        if(error?.response?.status === 406) {
          snackbar.show("error", t("deliveryNote.messages.downloadExcelMaxRowsExceeded"));
        } else{
          snackbar.show("error", t("deliveryNote.messages.downloadErrorMessage"));
        }
      });

      setLoading(false);
  };

  const columns: ColDef[] = [
    {
      headerName: t("deliveryNote.table.number"),
      field: nameof<OperationTypes.DeliveryNote>("number"),
      minWidth: 250,
    },
    {
      headerName: t("deliveryNote.table.transferRequestNumber"),
      field: `${nameof<OperationTypes.DeliveryNote>(
        "transferRequest"
      )}.${nameof<OperationTypes.TransferRequest>("number")}`,
      minWidth: 250,
    },
    {
      headerName: t("deliveryNote.table.startStationId"),
      field: `${nameof<OperationTypes.DeliveryNote>(
        "transferRequest"
      )}.${nameof<OperationTypes.TransferRequest>("startStationDescription")}`,
      minWidth: 200,
      cellRenderer: (props: ICellRendererParams<OperationTypes.DeliveryNote>) =>
        startStatationDescr(props),
    },
    {
      headerName: t("deliveryNote.table.startDateFrom"),
      field: `${nameof<OperationTypes.DeliveryNote>(
        "transferRequest"
      )}.${nameof<OperationTypes.TransferRequest>("realStartDate")}`,
      minWidth: 200,
      cellRenderer: (
        props: ICellRendererParams<OperationTypes.DeliveryNote>
      ) => formatDateTable(props.value, props?.data?.transferRequest?.startTimeZoneId ),
    },
    {
      headerName: t("deliveryNote.table.expectedEndStationId"),
      field: `${nameof<OperationTypes.DeliveryNote>(
        "transferRequest"
      )}.${nameof<OperationTypes.TransferRequest>(
        "expectedEndStationDescription"
      )}`,
      minWidth: 250,
      cellRenderer: (props: ICellRendererParams<OperationTypes.DeliveryNote>) =>
        expectedStatationDescr(props),
    },
    {
      headerName: t("deliveryNote.table.expectedEndDate"),
      field: `${nameof<OperationTypes.DeliveryNote>(
        "transferRequest"
      )}.${nameof<OperationTypes.TransferRequest>("expectedEndDate")}`,
      minWidth: 250,
      cellRenderer: (props: ICellRendererParams<OperationTypes.DeliveryNote>) =>
        formatDateTable(props.value, props?.data?.transferRequest?.expectedTimeZoneId ),
    },
    {
      headerName: t("deliveryNote.table.endStationId"),
      field: `${nameof<OperationTypes.DeliveryNote>(
        "transferRequest"
      )}.${nameof<OperationTypes.TransferRequest>("endStationDescription")}`,
      minWidth: 250,
      cellRenderer: (props: ICellRendererParams<OperationTypes.DeliveryNote>) =>
        endStatationDescr(props),
    },
    {
      headerName: t("deliveryNote.table.endDate"),
      field: `${nameof<OperationTypes.DeliveryNote>(
        "transferRequest"
      )}.${nameof<OperationTypes.TransferRequest>("endDate")}`,
      minWidth: 250,
      cellRenderer: (props: ICellRendererParams<OperationTypes.DeliveryNote>) =>
        formatDateTable(props.value, props?.data?.transferRequest?.endTimeZoneId),
    },
    {
      headerName: "",
      maxWidth: 100,
      minWidth: 100,
      pinned: "right",
      cellStyle: { "background-color": "primary.main" },
      cellRenderer: (
        props: ICellRendererParams<OperationTypes.DeliveryNote>
      ) => {
        return (
          <Mui.Stack
            direction="row"
            style={{ color: customTheme.palette.neutral.bright }}
          >
            <Mui.Tooltip title={t("deliveryNote.table.tooltip.details")}>
              <Mui.IconButton
                color="accent"
                onClick={() => showDetails(props.data?.id)}
              >
                <MuiIcons.Description />
              </Mui.IconButton>
            </Mui.Tooltip>

            <Mui.Tooltip title={"download"}>
              <Mui.IconButton
                color="accent"
                onClick={() => downloadPdf(props.data?.id)}
              >
                <MuiIcons.Download />
              </Mui.IconButton>
            </Mui.Tooltip>
          </Mui.Stack>
        );
      },
    },
  ];

  const locations = useMemo<AutoCompleteOption[]>(() => {
    return (
      allLocations.map((loc) => ({
        label: loc.description || "",
        value: loc.key || "",
      })) || []
    );
  }, [allLocations]);

  return (
    <Mui.Box>
      <SectionHeader
        title={
          t("deliveryNote.titleFirst") + " " + t("deliveryNote.titleSecond")
        }
      />
      <form
        id="delivery-note-search-form"
        onSubmit={props.form.handleSubmit(() => {})}
      >
        <GenericFilterBox
          formId="delivery-note-search-form"
          onReset={props.form.reset}
        >
          {/* Plate */}
          <FormInput
            name="plate"
            label={t("deliveryNote.filter.plate")}
            formInstance={props.form}
            type="text"
            fullWidth
          />
          {/* Vin */}
          <FormInput
            name="vin"
            label={t("deliveryNote.filter.vin")}
            formInstance={props.form}
            type="text"
            fullWidth
          />
          {/* Number */}
          <FormInput
            name="number"
            label={t("deliveryNote.filter.number")}
            formInstance={props.form}
            type="text"
            fullWidth
          />
          {/* Start Province */}
          <FormInput
            type="select"
            name="startStationId"
            fullWidth
            options={locations}
            label={t("deliveryNote.filter.startStationId")}
            //formInstance={form}
            value={props.form.watch("startStationId")}
            onChange={(newStatusValue) => {
              props.form.setValue("startStationId", newStatusValue);
            }}
          />
          {/* Start Date from */}
          <FormInput
            type="date"
            fullWidth
            name="startDateFrom"
            ampm={false}
            inputFormat={"DD/MM/YYYY HH:mm"}
            label={t("deliveryNote.filter.startDateFrom")}
            //formInstance={form}
            value={props.form.watch("startDateFrom")}
            onChange={(newTodayValue) => {
              props.form.setValue("startDateFrom", newTodayValue);
            }}
            // onViewChange={(view) => {
            //     if (view === "hours") {
            //         const newVal = dayjs(form.watch("startDateFrom")).set("hour", dayjs().get("hour")).set("minute", dayjs().get("minute"));
            //         form.setValue("startDateFrom", newVal.toISOString());
            //     }
            // }}
          />
          {/* Start Date to */}
          <FormInput
            type="date"
            fullWidth
            name="startDateTo"
            ampm={false}
            inputFormat={"DD/MM/YYYY HH:mm"}
            label={t("deliveryNote.filter.startDateTo")}
            //formInstance={form}
            value={props.form.watch("startDateTo")}
            onChange={(newTodayValue) => {
              props.form.setValue("startDateTo", newTodayValue);
            }}
            // onViewChange={(view) => {
            //     if (view === "hours") {
            //         const newVal = dayjs(form.watch("startDateTo")).set("hour", dayjs().get("hour")).set("minute", dayjs().get("minute"));
            //         form.setValue("startDateTo", newVal.toISOString());
            //     }
            // }}
          />
          {/* End Station */}
          <FormInput
            type="select"
            name="endStationId"
            fullWidth
            options={locations}
            label={t("deliveryNote.filter.endStationId")}
            //formInstance={form}
            value={props.form.watch("endStationId")}
            onChange={(newStatusValue) => {
              props.form.setValue("endStationId", newStatusValue);
            }}
          />

          {/* End Date from */}

          <FormInput
            type="date"
            fullWidth
            name="endDateFrom"
            ampm={false}
            inputFormat={"DD/MM/YYYY HH:mm"}
            label={t("deliveryNote.filter.endDateFrom")}
            //formInstance={form}
            value={props.form.watch("endDateFrom")}
            onChange={(newTodayValue) => {
              props.form.setValue("endDateFrom", newTodayValue);
            }}
            // onViewChange={(view) => {
            //     if (view === "hours") {
            //         const newVal = dayjs(form.watch("endDateFrom")).set("hour", dayjs().get("hour")).set("minute", dayjs().get("minute"));
            //         form.setValue("endDateFrom", newVal.toISOString());
            //     }
            // }}
          />
          {/* End Date to */}
          <FormInput
            type="date"
            fullWidth
            name="endDateTo"
            ampm={false}
            inputFormat={"DD/MM/YYYY HH:mm"}
            label={t("deliveryNote.filter.endDateTo")}
            //formInstance={form}
            value={props.form.watch("endDateTo")}
            onChange={(newTodayValue) => {
              props.form.setValue("endDateTo", newTodayValue);
            }}
            // onViewChange={(view) => {
            //     if (view === "hours") {
            //         const newVal = dayjs(form.watch("endDateTo")).set("hour", dayjs().get("hour")).set("minute", dayjs().get("minute"));
            //         form.setValue("endDateTo", newVal.toISOString());
            //     }
            // }}
          />
        </GenericFilterBox>
      </form>
      <Mui.Box ml={2} mr={2}>
        <Mui.Card>
        <Mui.Box sx={{ p: 2 }}>             
            <Mui.Grid
              container
              spacing={1.5}
              alignItems="center"
              direction="row"
              justifyContent="flex-end"
            >
              <Mui.Grid item>
                  <Mui.Button
                    variant="text"
                    size="icon"
                    onClick={handleDownloadExcel}
                    color="accent"
                  >
                    <Mui.Tooltip title={t("tooltipTitle.download")}>
                      <FileDownloadIcon />
                    </Mui.Tooltip>
                  </Mui.Button>
                </Mui.Grid>                
            </Mui.Grid>
          </Mui.Box>
          <AgGridOperationTable
            //gridRef={gridEquipmentKitsRef}
            columnDefs={columns}
            serverSideDatasource={deliveryNoteDataSource}
            rowModelType="serverSide"
            serverSideInfiniteScroll={true}
            cacheBlockSize={10}
            rowStyle={{ width: "250%" }}
            //rowSelection="single"
            //onSelectionChanged={onSelectionChanged}
            totalElements={totalDeliveryNote}
            emptyState={{
              title:t("deliveryNote.table.titleEmpty"),
              content:t("deliveryNote.table.contentEmpty")
            }}
          />
        </Mui.Card>
      </Mui.Box>

      {openDownloadModal && <ExportDeliveryNotesDialog
        setOpenDownloadModal={setOpenDownloadModal}
      />}

    </Mui.Box>
  );
};
